import axios, { AxiosError } from 'axios';
import { config } from '../config/app.config';
import { handleAuthError, logError, isAuthError } from '../utils/apiErrorHandler';

const apiClient = axios.create({
    baseURL: config.apiUrl,
    withCredentials: true, // Important for HTTP-only cookies
    // No timeout by default so long-running server tasks (large uploads, batch processing)
    // can complete. Individual requests may still set a shorter timeout if desired.
    timeout: 0,
});

/**
 * Response interceptor to handle errors
 * Redirects to login on 400, 401, 403 errors
 */
apiClient.interceptors.response.use(
    // Success response - pass through
    (response) => response,

    // Error response - handle and redirect
    (error: AxiosError) => {
        // Log the error for debugging
        logError(error, `${error.config?.method?.toUpperCase()} ${error.config?.url}`);

        // Allow callers to opt-out of the global auth-redirect by setting
        // a request header `x-skip-auth-redirect`. This is used for non-
        // blocking background checks where we don't want a full-page
        // navigation when the session is expired.
        const skipRedirect = Boolean((error.config as any)?.headers?.['x-skip-auth-redirect']);
        if (!skipRedirect && isAuthError(error)) {
            handleAuthError(error);
        }

        // Normalize API error message so callers get a useful message
        try {
            const resp = (error as any).response;
            if (resp && resp.data) {
                const data = resp.data;
                // Prefer explicit 'message', then structured 'detail'
                let apiMessage: string | null = null;
                if (typeof data.message === 'string' && data.message.trim()) {
                    apiMessage = data.message;
                } else if (data.detail) {
                    if (typeof data.detail === 'string') apiMessage = data.detail;
                    else if (Array.isArray(data.detail)) {
                        // join validation errors into a single string
                        apiMessage = data.detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('; ');
                    } else if (typeof data.detail === 'object') {
                        apiMessage = data.detail.message || JSON.stringify(data.detail);
                    }
                } else if (typeof resp.data === 'string') {
                    apiMessage = resp.data;
                }

                if (apiMessage) {
                    // overwrite the Error message so existing callers that read `e.message` get the API message
                    (error as any).message = apiMessage;
                    // also attach raw API detail for callers that want structured info
                    (error as any).apiDetail = data.detail;
                }
            }
        } catch (err) {
            // ignore normalization errors
        }

        // Return error to be handled by calling code
        return Promise.reject(error);
    }
);

export default apiClient;
